import { Children, isValidElement, useEffect, useMemo, useRef, useState, type ComponentType, type HTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import { MDXProvider } from '@mdx-js/react';
import SEO, { jsonLd, type Crumb } from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import FragmentLink from '@/components/FragmentLink';
import LastUpdated from '@/components/LastUpdated';
import TLDR from '@/components/TLDR';
import FAQ from '@/components/FAQ';
import CTABand from '@/components/CTABand';
import { INQUIRY_COPY, inquiryHref, inquiryTopic } from '@/lib/engagements';
import { mdxComponents } from '@/components/MdxComponents';
import { contentByPath, SECTION_LABEL, staticPageOrNull, type ContentMeta } from '@/lib/content';
import { humanizeSlug } from '@/lib/format';
import { AUTHOR, SITE_NAME, SITE_ORIGIN, absoluteUrl } from '@/lib/site';
import { tools } from '@/tools/registry';
import '@/components/reading-experience.css';

interface ArticleSection { id: string; title: string }

const headingText = (children: ReactNode): string => Children.toArray(children).map((child) => {
  if (typeof child === 'string' || typeof child === 'number') return String(child);
  return isValidElement<{ children?: ReactNode }>(child) ? headingText(child.props.children) : '';
}).join('');

const ArticleHeading = ({ children, id, ...rest }: HTMLAttributes<HTMLHeadingElement>) => {
  const slug = headingText(children).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return <h2 {...rest} id={id || `guide-${slug}`} tabIndex={-1}>{children}</h2>;
};

const articleComponents = { ...mdxComponents, h2: ArticleHeading };

const ContentsLinks = ({ sections, active, onSelect }: { sections: ArticleSection[]; active: string; onSelect: (id: string) => void }) => (
  <ol className="ecm-article-contents">
    {sections.map((section, index) => (
      <li key={section.id}>
        <FragmentLink hash={`#${section.id}`} aria-current={active === section.id ? 'location' : undefined} onClick={() => onSelect(section.id)}>
          <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
          <span>{section.title}</span>
        </FragmentLink>
      </li>
    ))}
  </ol>
);

interface ContentPageProps {
  meta: ContentMeta;
  Body: ComponentType<{ components?: Record<string, ComponentType<any>> }>;
}

// Label for a `related` path: a content title, a hub title, a tool title, or a humanized slug.
const relatedLabel = (path: string) => {
  const c = contentByPath(path);
  if (c) return c.title;
  const s = staticPageOrNull(path);
  if (s) return s.title;
  const t = tools.find((x) => `/tools/${x.slug}` === path);
  if (t) return t.title;
  return humanizeSlug(path.split('/').filter(Boolean).pop() ?? path);
};

// Generic renderer for every /<section>/<slug> page — CONTENT-SPEC §4:
// breadcrumbs → H1 → Last updated → TL;DR → body → FAQ → Related → CTA band.
const ContentPage = ({ meta, Body }: ContentPageProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [sections, setSections] = useState<ArticleSection[]>([]);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    // Print all FAQ answers, then restore each reader's disclosure state.
    let openedForPrint: HTMLDetailsElement[] = [];
    const beforePrint = () => {
      if (openedForPrint.length) return;
      openedForPrint = Array.from(document.querySelectorAll<HTMLDetailsElement>('.ecm-article details:not([open])'));
      openedForPrint.forEach((details) => { details.open = true; });
    };
    const afterPrint = () => {
      openedForPrint.forEach((details) => { details.open = false; });
      openedForPrint = [];
    };
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      afterPrint();
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [meta.path]);

  useEffect(() => {
    const headings = Array.from(bodyRef.current?.querySelectorAll<HTMLHeadingElement>('h2') ?? []);
    const seen = new Map<string, number>();
    const nextSections = headings.map((heading) => {
      const baseId = heading.id;
      const count = seen.get(baseId) ?? 0;
      seen.set(baseId, count + 1);
      if (count) heading.id = `${baseId}-${count + 1}`;
      return { id: heading.id, title: heading.textContent ?? '' };
    });
    setSections(nextSections);
    setActiveSection('');
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveSection(entry.target.id);
      });
    }, { rootMargin: '-12% 0px -68% 0px', threshold: 0 });
    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [meta.path, Body]);

  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: SECTION_LABEL[meta.section], href: `/${meta.section}` },
    { name: meta.title, href: meta.path },
  ];

  const articleSchema = useMemo(() => {
    const url = absoluteUrl(meta.path);
    const base = {
      '@context': 'https://schema.org',
      // Legacy HowTo frontmatter has no structured step definitions. Describe
      // those pages as technical articles instead of inventing procedural data.
      '@type': meta.schema === 'HowTo' ? 'TechArticle' : meta.schema,
      headline: meta.title,
      description: meta.description,
      datePublished: meta.date,
      dateModified: meta.updated,
      inLanguage: 'en-US',
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      url,
      keywords: meta.tags.join(', '),
      author: {
        '@type': 'Person',
        '@id': `${SITE_ORIGIN}/about#engineer-heading`,
        name: AUTHOR.name,
        jobTitle: AUTHOR.jobTitle,
        url: `${SITE_ORIGIN}/about#engineer-heading`,
      },
      publisher: {
        '@type': 'Organization',
        '@id': `${SITE_ORIGIN}/#organization`,
        name: SITE_NAME,
        logo: { '@type': 'ImageObject', url: `${SITE_ORIGIN}/icon-512.png` },
      },
      ...(meta.review ? {
        citation: meta.review.sources.map((source) => ({
          '@type': 'CreativeWork',
          name: source.label,
          url: source.url,
        })),
      } : {}),
    };
    return base;
  }, [meta]);

  return (
    <>
      <SEO
        title={meta.seoTitle ?? meta.title}
        description={meta.description}
        path={meta.path}
        ogType="article"
        breadcrumbs={crumbs}
      />
      <Head>
        <script type="application/ld+json">{jsonLd(articleSchema)}</script>
      </Head>

      <article className="ecm-article">
        <header className="ecm-article-header on-forest">
          <div className="container">
            <Breadcrumbs items={crumbs} />
            <div className="ecm-article-heading-row">
              <div>
                <p className="ecm-article-kicker">{SECTION_LABEL[meta.section]} <span aria-hidden="true">/</span> {meta.section === 'notes' ? 'From the field' : 'Technical guide'}</p>
                <h1>{meta.title}</h1>
              </div>
              <svg className="ecm-article-mark" width="124" height="156" viewBox="0 0 124 156" fill="none" aria-hidden="true">
                <path d="M20 10h72l20 20v110H20V10Z" stroke="currentColor" />
                <path d="M92 10v20h20M12 22v126h90M34 54h60M34 66h44M34 96h60M34 108h44" stroke="currentColor" />
                <path d="M34 82h18" stroke="#B58B3A" strokeWidth="2" />
              </svg>
            </div>
            <div className="ecm-article-metadata">
              <p><span>By {AUTHOR.name}</span><span>{AUTHOR.jobTitle}</span></p>
              <LastUpdated date={meta.updated} readingTime={meta.readingTime} />
            </div>
          </div>
        </header>

        <div className="container ecm-article-layout">
          <aside className="ecm-article-sidebar" aria-label="Article navigation">
            <div className="ecm-article-sidebar-inner">
              {sections.length > 0 && (
                <nav aria-label="On this page">
                  <h2>On this page</h2>
                  <ContentsLinks sections={sections} active={activeSection} onSelect={setActiveSection} />
                </nav>
              )}
              <div className="ecm-article-sidebar-contact">
                <p>Working through this in your estate?</p>
                <Link to={inquiryHref(meta.path)}>
                  {INQUIRY_COPY[inquiryTopic(meta.path)].label} <span aria-hidden="true">↗</span>
                </Link>
              </div>
              {meta.tags.length > 0 && (
                <div className="ecm-article-topics">
                  <h2>In this guide</h2>
                  <ul>{meta.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
                </div>
              )}
            </div>
          </aside>

          <div className="ecm-article-main">
            {sections.length > 0 && (
              <details className="ecm-article-mobile-contents">
                <summary>On this page <span aria-hidden="true">+</span></summary>
                <nav aria-label="On this page">
                  <ContentsLinks sections={sections} active={activeSection} onSelect={setActiveSection} />
                </nav>
              </details>
            )}
            <button type="button" onClick={() => window.print()} className="ecm-article-print mb-6 text-small underline underline-offset-4">Print or save this guide as PDF</button>
            <TLDR text={meta.tldr} />

            {meta.review && (
              <section aria-labelledby="scope-evidence-heading" className="mt-6 border border-rule bg-cream-light p-5 sm:p-6">
                <h2 id="scope-evidence-heading" className="text-small">Scope and evidence</h2>
                <dl className="mt-4 space-y-3 text-small">
                  <div>
                    <dt className="font-semibold">Scope</dt>
                    <dd className="mt-1">{meta.review.scope}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold">Evidence basis</dt>
                    <dd className="mt-1">{meta.review.basis}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold">Sources checked</dt>
                    <dd className="mt-1"><time dateTime={meta.review.checked}>{meta.review.checked}</time></dd>
                  </div>
                </dl>
                <ul className="mt-4 list-disc space-y-2 pl-5 text-small">
                  {meta.review.sources.map((source) => (
                    <li key={source.url}><a href={source.url} className="link">{source.label}</a></li>
                  ))}
                </ul>
              </section>
            )}

            <div className="prose ecm-article-body" ref={bodyRef}>
              <MDXProvider components={articleComponents}>
                <Body components={articleComponents} />
              </MDXProvider>
            </div>

            {meta.faq && meta.faq.length > 0 && (
              <div className="mt-16">
                <FAQ items={meta.faq} />
              </div>
            )}

            {meta.related && meta.related.length > 0 && (
              <section aria-labelledby="related-heading" className="ecm-article-related">
                <h2 id="related-heading" className="text-subhead">
                  Continue reading
                </h2>
                <ul>
                  {meta.related.map((p) => (
                    <li key={p}>
                      <Link to={p}>
                        <span>{relatedLabel(p)}</span><span aria-hidden="true">↗</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </article>

      <CTABand variant={meta.cta} />
    </>
  );
};

export default ContentPage;
